import { toPlace, type DisplayMessageType, type Place } from '../../types/display'

/** Everything the display relies on from the API, in one place. The source of
 *  truth is the contract on lovingloyalty-api#626, "CFD integration contract -
 *  shared device requests". If the API changes a name, this file changes and
 *  nothing else should have to. */
export const CONTRACT_VERSION = 2

export const STREAM_PATH = '/places/active-orders/stream'

/** Largest display message the till may send, as Reactivate bounds it. */
export const PAYLOAD_MAX_BYTES = 100 * 1024

/** How long the API holds a payment moment valid. A moment older than this is
 *  never shown, including on a reconnect; the cart and welcome have no limit. */
export const MOMENT_LIFETIME_MS: Partial<Record<DisplayMessageType, number>> = {
  payment_approved: 6_000,
  payment_error: 6_000,
  change_given: 6_000,
  cashback_given: 6_000,
  scan_mobilepay_qr_code: 120_000,
  scan_receipt_qr_code: 120_000,
}

/** Settled statuses fence a row: it can never show again. */
export const SETTLED_STATUSES = ['expired', 'cancelled', 'rejected', 'unknown'] as const
const STATUSES: readonly string[] = ['requested', 'delivered', 'fulfilled', ...SETTLED_STATUSES]

/** Close codes that mean the grant is no good, so stop. 4400 and 1009 mean
 *  our own hello was wrong, and sending it again will not fix it. */
export const REFUSED_CLOSE_CODES: readonly number[] = [1008, 1009, 4400, 4401, 4403]

const BRANDING_KEYS = ['title', 'tagline', 'logo', 'currency', 'google_place_id'] as const

/** Who this display is: the store and the owner the grant must be for. */
export interface Scope {
  placeId: number
  userId: number
}

/** One display_update row, from a socket frame or a REST read. */
export interface Row {
  id: number
  status: string
  payload: {
    message: Record<string, unknown> | null
    invalidated: boolean
    serverUpdatedAtMs: number
  }
}

export type LatestState =
  | { kind: 'current'; row: unknown; serverNowMs: number }
  | { kind: 'empty'; serverNowMs: number }

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Ids come as numbers in REST and decimal strings on the socket. */
export function positiveInt(value: unknown): number | null {
  const n = typeof value === 'string' && /^[1-9][0-9]*$/.test(value) ? Number(value) : value
  return typeof n === 'number' && Number.isSafeInteger(n) && n > 0 ? n : null
}

export function isSettled(status: string): boolean {
  return (SETTLED_STATUSES as readonly string[]).includes(status)
}

export function streamUrl(apiUrl: string): string | null {
  try {
    const url = new URL(STREAM_PATH, apiUrl)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
    return url.toString()
  } catch {
    return null
  }
}

/** All five keys, each a string or null. Nulls are dropped so the display's
 *  own defaults apply. */
function parseBranding(value: unknown): Place | null {
  if (!isRecord(value)) return null
  for (const key of BRANDING_KEYS) {
    if (!(key in value) || (value[key] !== null && typeof value[key] !== 'string')) return null
  }
  return toPlace(value)
}

/** The ready frame, accepted only when it is for exactly this store and owner
 *  and carries the shop's branding. Anything else is a refusal. */
export function parseReady(frame: Record<string, unknown>, scope: Scope): { branding: Place } | null {
  const targets = frame.request_targets
  const types = frame.request_types
  const branding = parseBranding(frame.branding)
  const ok =
    frame.v === 1 &&
    frame.place_id === scope.placeId &&
    frame.requests === true &&
    Array.isArray(targets) &&
    targets.length === 1 &&
    isRecord(targets[0]) &&
    targets[0].type === 'customer_display' &&
    targets[0].id === scope.userId &&
    Array.isArray(types) &&
    types.length === 1 &&
    types[0] === 'display_update' &&
    typeof frame.server_now_ms === 'number' &&
    Number.isFinite(frame.server_now_ms)
  return ok && branding ? { branding } : null
}

/** A request frame as the row it announces, or null if it is not one for this
 *  display. The socket carries `request_id`; REST rows carry `id`. */
export function requestFrameRow(frame: Record<string, unknown>): { raw: Record<string, unknown>; serverNowMs: number; stored: boolean } | null {
  if (frame.kind !== 'request' || frame.type !== 'display_update') return null
  if (typeof frame.server_now_ms !== 'number') return null
  return { raw: { ...frame, id: frame.request_id }, serverNowMs: frame.server_now_ms, stored: frame.stored === true }
}

/** A row from either path, checked against this display's scope. REST sends
 *  the payload as a JSON string, the socket as an object. */
export function parseRow(raw: unknown, scope: Scope, serverNowMs: number): Row | null {
  if (!isRecord(raw)) return null
  const id = positiveInt(raw.id)
  if (
    id === null ||
    positiveInt(raw.place_id) !== scope.placeId ||
    raw.type !== 'display_update' ||
    raw.target_type !== 'customer_display' ||
    positiveInt(raw.target_id) !== scope.userId ||
    typeof raw.status !== 'string' ||
    !STATUSES.includes(raw.status)
  )
    return null

  let payload = raw.payload
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload)
    } catch {
      return null
    }
  }
  if (
    !isRecord(payload) ||
    payload.v !== 1 ||
    typeof payload.server_updated_at_ms !== 'number' ||
    !Number.isFinite(payload.server_updated_at_ms) ||
    payload.server_updated_at_ms < 0 ||
    serverNowMs < payload.server_updated_at_ms ||
    !(payload.message === null || isRecord(payload.message)) ||
    (payload.invalidated === true && payload.message !== null)
  )
    return null
  if (new TextEncoder().encode(JSON.stringify(payload.message)).byteLength > PAYLOAD_MAX_BYTES) return null

  return {
    id,
    status: raw.status,
    payload: {
      message: payload.message as Record<string, unknown> | null,
      invalidated: payload.invalidated === true,
      serverUpdatedAtMs: payload.server_updated_at_ms,
    },
  }
}

/** The latest-state read: one current row, or a confirmed empty screen.
 *  Anything else is unknown, which is not the same as empty. */
export function parseLatest(data: unknown): LatestState | null {
  if (!isRecord(data) || !isRecord(data.display_state)) return null
  const state = data.display_state
  const serverNowMs = state.server_now_ms
  if (typeof serverNowMs !== 'number' || !Number.isFinite(serverNowMs)) return null
  const node = data['node.device_request']
  if (!isRecord(node)) return null
  const rows = Object.values(node)
  if (state.status === 'current' && rows.length === 1) return { kind: 'current', row: rows[0], serverNowMs }
  if (state.status === 'empty' && rows.length === 0) return { kind: 'empty', serverNowMs }
  return null
}
